/**
 * Centralized payment error logging.
 * Har type ka error (network, backend, frontend, payload, header) log hota hai
 * taake koi bhi log miss na ho — payment, events booking, membership sab flows.
 * Frontend errors bhi PaymentErrorLog table mein save hote hain via report-client API.
 */

import axiosInstance, { getAPIURL } from "../api/axiosInstance";

export type PaymentFlow =
  | "membership"
  | "event-booking"
  | "investor-checkout"
  | "company-checkout"
  | "payment-api";

export type PaymentErrorSource =
  | "network"
  | "backend"
  | "frontend"
  | "payload"
  | "header"
  | "stripe"
  | "unknown";

export interface PaymentErrorLogParams {
  flow: PaymentFlow;
  errorSource: PaymentErrorSource;
  message: string;
  error?: unknown;
  requestConfig?: {
    url?: string;
    method?: string;
    baseURL?: string;
    headers?: Record<string, string>;
    params?: unknown;
  };
  requestPayload?: unknown;
  responseStatus?: number;
  responseData?: unknown;
  /** Optional: sent to API so DB log has user context. If not provided, current login user context is used when set. */
  userId?: string;
  userEmail?: string;
  userName?: string;
  productId?: string;
}

/** Current login user context for client error reports (set from AppContext so N/A fields are filled). */
let currentUserContext: {
  userId?: string;
  userEmail?: string;
  userName?: string;
} = {};

/**
 * Set the current user context so ClientError logs save with user id/email/name instead of N/A.
 * Call this from AppContext when user is loaded (e.g. when user or objectId is set).
 */
export function setPaymentLogUserContext(ctx: {
  userId?: string;
  userEmail?: string;
  userName?: string;
}): void {
  currentUserContext = { ...ctx };
}

const PAYMENT_LOG_PREFIX = "[PaymentError]";

function sanitizeHeaders(headers?: Record<string, unknown>): Record<string, string> {
  if (!headers || typeof headers !== "object") return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === "authorization") {
      out[k] = v && typeof v === "string" ? "[REDACTED]" : String(v);
    } else if (v != null) {
      out[k] = typeof v === "string" ? v : JSON.stringify(v);
    }
  }
  return out;
}

function sanitizePayload(payload: unknown): unknown {
  if (payload == null) return payload;
  if (typeof payload !== "object") return payload;
  const safe: Record<string, unknown> = {};
  const skipKeys = ["cardNumber", "cvc", "cvc2", "card_cvc"];
  for (const [key, value] of Object.entries(payload)) {
    const lower = key.toLowerCase();
    if (skipKeys.some((s) => lower.includes(s))) {
      safe[key] = "[REDACTED]";
    } else {
      safe[key] = value;
    }
  }
  return safe;
}

/**
 * Log payment error with full context. Console + structured object.
 * Also sends to backend so error is saved in PaymentErrorLog table (admin panel).
 * Fire-and-forget: API failure is ignored so UI is never blocked.
 */
export function logPaymentError(params: PaymentErrorLogParams): void {
  const {
    flow,
    errorSource,
    message,
    error,
    requestConfig,
    requestPayload,
    responseStatus,
    responseData,
    userId: paramUserId,
    userEmail: paramUserEmail,
    userName: paramUserName,
    productId,
  } = params;

  const userId = paramUserId ?? currentUserContext.userId;
  const userEmail = paramUserEmail ?? currentUserContext.userEmail;
  const userName = paramUserName ?? currentUserContext.userName;

  const sanitizedHeaders = requestConfig?.headers
    ? sanitizeHeaders(requestConfig.headers as Record<string, unknown>)
    : undefined;
  const sanitizedPayload =
    requestPayload !== undefined ? sanitizePayload(requestPayload) : undefined;

  const logPayload = {
    timestamp: new Date().toISOString(),
    flow,
    errorSource,
    message,
    request: requestConfig
      ? {
          url: requestConfig.url,
          method: requestConfig.method,
          baseURL: requestConfig.baseURL,
          headers: sanitizedHeaders,
          params: requestConfig.params,
          payload: sanitizedPayload,
        }
      : undefined,
    response:
      responseStatus !== undefined || responseData !== undefined
        ? { status: responseStatus, data: responseData }
        : undefined,
    errorSummary:
      error != null
        ? {
            name: error instanceof Error ? error.name : undefined,
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
          }
        : undefined,
  };

  console.error(PAYMENT_LOG_PREFIX, message, logPayload);

  if (error instanceof Error && error.stack) {
    console.error(PAYMENT_LOG_PREFIX, "Stack:", error.stack);
  }

  // Save to DB (PaymentErrorLog) so admin panel shows frontend errors too. Fire-and-forget.
  try {
    const requestDetailsJson =
      logPayload.request || logPayload.response
        ? JSON.stringify({
            request: logPayload.request,
            response: logPayload.response,
            errorSummary: logPayload.errorSummary,
          })
        : undefined;

    axiosInstance
      .post(getAPIURL("/PaymentErrorLog/report-client"), {
        flow,
        errorSource,
        message,
        requestUrl: requestConfig?.url,
        requestMethod: requestConfig?.method,
        responseStatus: responseStatus ?? undefined,
        requestDetailsJson,
        userId: userId || undefined,
        userEmail: userEmail || undefined,
        userName: userName || undefined,
        productId: productId ?? undefined,
      })
      .catch(() => {
        // Ignore: e.g. network down, 401; don't block or spam console
      });
  } catch {
    // Ignore if building payload or axios fails
  }
}

/**
 * Helper: derive error source from axios error.
 */
export function getPaymentErrorSource(err: unknown): PaymentErrorSource {
  if (!err || typeof err !== "object") return "unknown";
  const ax = err as { response?: unknown; request?: unknown; message?: string };
  if (ax.response) return "backend";
  if (ax.request && !ax.response) return "network";
  if (ax.message?.toLowerCase().includes("network") || ax.message?.toLowerCase().includes("timeout"))
    return "network";
  return "unknown";
}

/**
 * Helper: extract user-facing message from axios error for UI.
 */
export function getPaymentErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const res = (err as { response?: { data?: { error?: string }; status?: number } }).response;
    if (res?.data?.error && typeof res.data.error === "string") return res.data.error;
    if (res?.status === 401) return "Session expired. Please log in again.";
    if (res?.status && res.status >= 500) return "Server error. Please try again later.";
  }
  if (err instanceof Error) return err.message;
  return typeof err === "string" ? err : "Payment processing failed.";
}
